import React, { useEffect, useState, useRef } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { styled } from '@mui/system';
import store, { AppDispatch, RootState } from './redux/store';
import {theme, darkTheme} from './theme';
import AppRoutes from './Routes';
import { clearUserState, fetchUserData, refreshAccessToken, syncAccessToken } from './redux/actions/user.actions';
import { setupTokenRefresh, clearTokenRefresh } from './redux/utils/tokenManager';
import { listenForTokenChanges } from './redux/utils/tokenSync';
import ImpersonateDialog from './components/common/Dialog/ImpersonateDialog';
import roleCheck from './components/Layout/roleCheck';
import axios from 'axios';

const AppWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
});

const MainContent = styled('main')({
  flexGrow: 1,
});

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { accessToken, expirationTime, data:user } = useSelector((state: RootState) => state.user);
  const [open, setOpen] = useState(false);
  const userRef = useRef(user);
  
  // Track if we've set up token refresh to avoid resetting it unnecessarily
  const tokenRefreshSetupRef = useRef<number | null>(null);
  
  // Update ref when user changes
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Track the in-flight refresh promise so queued requests can wait on it
  const refreshPromiseRef = useRef<Promise<any> | null>(null);

  useEffect(() => {
    // Global axios response interceptor to handle invalid/expired tokens
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const msg = error?.response?.data?.data?.message
          ?? error?.response?.data?.message
          ?? error?.message;

        if (msg === 'Invalid token.' && error.config && !error.config._retried) {
          const headers = error.config.headers ?? {};
          const usedToken = String(headers.Authorization ?? headers.authorization ?? '').replace(/^Bearer\s+/i, '');
          const currentToken = store.getState().user.accessToken;
          // The request went out before this tab picked up a newer token (e.g. from another
          // tab). Refreshing again would revoke that token, so just retry with it.
          const alreadyReplaced = Boolean(usedToken && currentToken && usedToken !== currentToken);

          if (alreadyReplaced) {
            // Fall through to the retry below.
          } else if (refreshPromiseRef.current) {
            // If a refresh is already in progress, wait for it
            try {
              await refreshPromiseRef.current;
            } catch {
              // Refresh failed — let the original error propagate
              return Promise.reject(error);
            }
          } else {
            // Start a new refresh
            const refreshAction = dispatch(refreshAccessToken());
            refreshPromiseRef.current = refreshAction;
            try {
              await refreshAction.unwrap();
            } catch {
              // Refresh failed — clearUserState is already dispatched inside the thunk
              refreshPromiseRef.current = null;
              return Promise.reject(error);
            }
            refreshPromiseRef.current = null;
          }

          // Retry the original request with the new token
          const newToken = store.getState().user.accessToken;
          if (newToken) {
            error.config._retried = true;
            error.config.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(error.config);
          }

          return Promise.reject(error);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [dispatch]);

  // Pick up logins, token refreshes and logouts from other tabs.
  useEffect(() => {
    return listenForTokenChanges(
      (token) => dispatch(syncAccessToken(token)),
      () => dispatch(clearUserState())
    );
  }, [dispatch]);

  useEffect(() => {
    var session = sessionStorage.getItem('impersonate');
    let canImpersonate = session!==null  // there is an impersonation in storage
                        && !['admin','developer','corporate'].includes(session)  // the impersonation is NOT of these roles
                        && roleCheck(userRef.current,['admin','developer','corporate']);  // the user IS one of these roles
    
    if (session && canImpersonate) {
        setOpen(canImpersonate);

    } else{
      setOpen(false);
    };
  }, [setOpen, user]);

    // Listen for impersonate events to show/hide dialog
    useEffect(() => {
      const handleImpersonate = (event: Event) => {
        var session = sessionStorage.getItem('impersonate');
        let canImpersonate = session!==null  // there is an impersonation in storage
                            && !['admin','developer','corporate'].includes(session)  // the impersonation is NOT of these roles
                            && roleCheck(userRef.current,['admin','developer','corporate']);  // the user IS one of these roles
        
        if (session && canImpersonate) {
            setOpen(canImpersonate);
        } else{
          setOpen(false);
        };
      };
  
      window.addEventListener("impersonate", handleImpersonate);
      
      return () => {
        window.removeEventListener("impersonate", handleImpersonate);
      };
    }, []);
  
    useEffect(() => {
      const now = Date.now();
      // If no access token or it's expired, try to refresh
      if ((!accessToken || !expirationTime || expirationTime < now) && localStorage.getItem('expirationTime')) {
        dispatch(refreshAccessToken());
        tokenRefreshSetupRef.current = null; // Clear setup when refreshing
      } else if (accessToken && expirationTime && expirationTime > now) {
        const impersonateRole = sessionStorage.getItem('impersonate');
        dispatch(fetchUserData(impersonateRole ? { impersonate_role: impersonateRole } : undefined));
        
        // Only setup token refresh if we haven't already set it up for this expiration time
        // This prevents clearing and resetting the timeout unnecessarily
        if (tokenRefreshSetupRef.current !== expirationTime) {
          console.log('[App] Setting up token refresh for expiration time:', new Date(expirationTime).toISOString());
          setupTokenRefresh(dispatch, expirationTime);
          tokenRefreshSetupRef.current = expirationTime;
        }
      }

    return () => {
      // Only clear if we're actually changing to a different expiration time or token is being cleared
      // Don't clear on every render - let the timeout fire naturally
      if (tokenRefreshSetupRef.current !== null && tokenRefreshSetupRef.current !== expirationTime) {
        console.log('[App] Clearing token refresh timeout (expiration time changed)');
        clearTokenRefresh();
      }
    };
  }, [dispatch, accessToken, expirationTime]);

  return (
    <AppWrapper>
      <MainContent>
        <ImpersonateDialog open={open} setOpen={setOpen}/>
        <AppRoutes />
      </MainContent>
    </AppWrapper>
  );
};

const App: React.FC = () => {
  const [mode,setMode] = useState('light');

  useEffect(() => {
    var local = localStorage.getItem('data-theme');
    if (local) {
      setMode(local);
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setMode('dark');
    };
  }, [setMode]);

  window.matchMedia('(prefers-color-scheme: dark)').addEventListener("change", function (e) {
    e.matches ? setMode('dark') : setMode('light');
  });

  window.addEventListener("storage", (event) => {
    var local = localStorage.getItem('data-theme');
    if (local) {
      setMode(local);
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setMode('dark');
    };
  });

  return (
    <Provider store={store}>
      <ThemeProvider theme={mode==='dark'? darkTheme : theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </Provider>
  );
};

export default App;